import { Type } from '@angular/core';

interface Unsubscribable {
  unsubscribe(): void;
}

type Constructor<T = object> = Type<T>;

type LifecycleHook = (...args: unknown[]) => void;

const isFunction = (fn: unknown): fn is LifecycleHook => typeof fn === 'function';

const doUnsubscribe = (subscription: Unsubscribable | unknown) => {
  if (subscription && isFunction((subscription as Unsubscribable).unsubscribe)) {
    (subscription as Unsubscribable).unsubscribe();
  }
};

const doUnsubscribeIfArray = (subscriptionsArray: Unsubscribable[] | unknown) => {
  if (Array.isArray(subscriptionsArray)) {
    subscriptionsArray.forEach(doUnsubscribe);
  }
};

interface AutoUnsubscribeOptions {
  blackList?: string[];
  arrayName?: string;
  event?: string;
}

export function AutoUnsubscribe({
  blackList = [],
  arrayName = '',
  event = 'ngOnDestroy',
}: AutoUnsubscribeOptions = {}) {
  return function (constructor: Constructor) {
    const original = constructor.prototype[event];

    if (!isFunction(original)) {
      throw new Error(
        `${constructor.name} is using @AutoUnsubscribe but does not implement ${event}`
      );
    }

    constructor.prototype[event] = function (...args: unknown[]) {
      if (isFunction(original)) {
        original.apply(this, args);
      }

      if (arrayName) {
        doUnsubscribeIfArray(this[arrayName]);
        return;
      }

      for (const propName in this) {
        if (blackList.includes(propName)) continue;

        const property = this[propName];
        doUnsubscribe(property);
      }
    };
  };
}
